import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { Link } from "react-router";
import { ExternalLink } from "lucide-react";

import type { inferRouterOutputs } from "@trpc/server";

import type { AppRouter } from "@runcheetah/api/router";
import {
  CodeText,
  DataTable,
  MoneyValue,
  PageHeader,
  QueryErrorState,
  SortableTableHeader,
  StatusPill,
  TableSkeleton,
} from "@/components/common";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type Brand = RouterOutputs["admin"]["brands"]["list"][number];
type BrandStatus = Brand["status"];

export const STATUS_CONFIG: Record<BrandStatus, { label: string; className: string }> = {
  queued: {
    label: "Queued",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  processing: {
    label: "Processing",
    className: "border-yellow-200 bg-yellow-50 text-yellow-700",
  },
  scanned: {
    label: "Scanned",
    className: "border-green-200 bg-green-50 text-green-700",
  },
  failed: {
    label: "Failed",
    className: "border-red-200 bg-red-50 text-red-700",
  },
};

export function StatusBadge({ status }: { status: BrandStatus }) {
  const config = STATUS_CONFIG[status];
  return <StatusPill label={config?.label || status} className={config?.className} />;
}

const formatDate = formatDateTime;

const columns: ColumnDef<Brand>[] = [
  {
    accessorKey: "sourceDomain",
    header: ({ column }) => <SortableTableHeader column={column}>Domain</SortableTableHeader>,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Link
          to={`/brands/${row.original.id}`}
          className="font-medium hover:underline text-foreground"
        >
          {row.original.sourceDomain}
        </Link>
        <a
          href={row.original.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground transition-colors"
          title={row.original.sourceUrl}
        >
          <ExternalLink className="size-3.5" />
        </a>
      </div>
    ),
  },
  {
    accessorKey: "ownerEmail",
    header: "Owner",
    cell: ({ row }) => {
      const ownerLabel = row.original.ownerName || row.original.ownerEmail;
      return ownerLabel ? (
        <Link
          to={row.original.ownerId ? `/users/${row.original.ownerId}` : "/users"}
          className="text-muted-foreground hover:text-foreground text-sm hover:underline"
        >
          {ownerLabel}
        </Link>
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "totalCostUsd",
    header: ({ column }) => <SortableTableHeader column={column}>Total Cost</SortableTableHeader>,
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">
        <MoneyValue amount={row.original.totalCostUsd} />
      </span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => <SortableTableHeader column={column}>Created</SortableTableHeader>,
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">{formatDate(row.original.createdAt)}</span>
    ),
  },
  {
    accessorKey: "updatedAt",
    header: "Last Updated",
    cell: ({ row }) => (
      <span className="text-muted-foreground text-sm">{formatDate(row.original.updatedAt)}</span>
    ),
  },
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ row }) => <CodeText>{row.original.id}</CodeText>,
  },
];

export function BrandsTable({ data }: { data: Brand[] }) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <DataTable table={table} columnsCount={columns.length} emptyMessage="No brands found." />;
}

export function BrandsPage() {
  const { data, isPending, error } = trpc.admin.brands.list.useQuery({});

  if (isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="Brands" meta="Loading..." />
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    const isForbidden = error.data?.code === "FORBIDDEN";
    return (
      <QueryErrorState
        title="Failed to load brands"
        message={error.message}
        isForbidden={isForbidden}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Brands"
        meta={`${data.length} ${data.length === 1 ? "brand" : "brands"}`}
      />
      <BrandsTable data={data} />
    </div>
  );
}
